import type { QuizConfig } from '../config/quiz.config';
import { QuizPhase, type QuizSession } from '../quiz/QuizSession';
import type { ShownQuestion } from '../quiz/quizPool';
import { el, formatNumber } from './dom';
import { ICONS, icon } from './icons';

const LETTERS = ['A', 'B', 'C', 'D'] as const;
/** Arrow keys move the marker (no answer yet: the first press marks the first / last answer); Enter or Space confirms. */
const SELECT_PREV: readonly string[] = ['ArrowUp', 'ArrowLeft', 'w', 'W'];
const SELECT_NEXT: readonly string[] = ['ArrowDown', 'ArrowRight', 's', 'S'];
const CONFIRM: readonly string[] = ['Enter', ' ', 'NumpadEnter'];

/**
 * Bau-Quiz panel over the frozen picture: question, three answer buttons (tap or keys), a time bar with the
 * seconds and the bonus counter running down. It only renders what the QuizSession says; answers go back
 * through `onAnswer`.
 */
export class QuizOverlay {
  readonly element: HTMLDivElement;
  private readonly category: HTMLDivElement;
  private readonly question: HTMLDivElement;
  private readonly answers: HTMLDivElement;
  private readonly timeFill: HTMLDivElement;
  private readonly seconds: HTMLSpanElement;
  private readonly bonus: HTMLSpanElement;
  private readonly result: HTMLDivElement;
  private buttons: HTMLButtonElement[] = [];
  /** Answer marked with the arrow keys (−1: none yet). */
  private selected = -1;
  private shownId: string | null = null;
  private resultShown = false;
  private suspended = false;

  constructor(
    root: HTMLElement,
    private readonly config: QuizConfig,
    private readonly onAnswer: (index: number) => void,
  ) {
    this.element = el('div', 'quiz', undefined, root);
    this.element.setAttribute('role', 'dialog');
    this.element.setAttribute('aria-label', 'Bau-Quiz');
    this.element.hidden = true;
    const panel = el('div', 'quiz-panel', undefined, this.element);
    const head = el('div', 'quiz-head', undefined, panel);
    head.appendChild(icon(ICONS.collect, 'quiz-icon'));
    el('div', 'quiz-title', 'BAU-QUIZ', head);
    this.category = el('div', 'quiz-category', '', head);
    const bonusBox = el('div', 'quiz-bonus', undefined, panel);
    bonusBox.appendChild(icon(ICONS.collect, 'quiz-bonus-icon'));
    this.bonus = el('span', 'quiz-bonus-value', '', bonusBox);
    el('span', 'quiz-bonus-label', 'Bonusflaschen', bonusBox);
    this.question = el('div', 'quiz-question', '', panel);
    this.answers = el('div', 'quiz-answers', undefined, panel);
    const time = el('div', 'quiz-time', undefined, panel);
    const bar = el('div', 'quiz-time-bar', undefined, time);
    this.timeFill = el('div', 'quiz-time-fill', undefined, bar);
    this.seconds = el('span', 'quiz-time-seconds', '', time);
    this.result = el('div', 'quiz-result', '', panel);
    this.result.setAttribute('aria-live', 'assertive');
    window.addEventListener('keydown', this.onKey, { capture: true });
  }

  /** Renders the session state (call every frame while the quiz is active). */
  update(session: QuizSession): void {
    const shown = session.question;
    if (!session.active || !shown) {
      this.hide();
      return;
    }
    if (shown.id !== this.shownId) this.show(shown);
    const share = session.secondsLeft / this.config.timeLimitSeconds;
    this.timeFill.style.transform = `scaleX(${share})`;
    this.element.classList.toggle('is-hurry', share < 0.4);
    this.seconds.textContent = `${Math.ceil(session.secondsLeft)} s`;
    if (session.phase === QuizPhase.Asking) this.bonus.textContent = formatNumber(session.bonus);
    if (session.phase === QuizPhase.Result && !this.resultShown) this.showResult(session, shown);
  }

  /**
   * Game paused (tab hidden) mid-question: the question disappears – no thinking it over at leisure – and keys go
   * to the pause screen again. The round goes on where it was once the game runs.
   */
  setSuspended(suspended: boolean): void {
    this.suspended = suspended;
    this.element.classList.toggle('is-suspended', suspended);
  }

  hide(): void {
    if (this.element.hidden) return;
    this.element.hidden = true;
    this.shownId = null;
  }

  dispose(): void {
    window.removeEventListener('keydown', this.onKey, { capture: true });
    this.element.remove();
  }

  private show(shown: ShownQuestion): void {
    this.shownId = shown.id;
    this.resultShown = false;
    this.element.hidden = false;
    this.element.classList.remove('is-correct', 'is-wrong', 'is-result', 'is-hurry');
    this.category.textContent = shown.category;
    this.question.textContent = shown.question;
    this.result.textContent = '';
    this.answers.replaceChildren();
    this.selected = -1;
    this.buttons = shown.answers.map((answer, i) => {
      const button = el('button', 'quiz-answer', undefined, this.answers);
      button.type = 'button';
      el('span', 'quiz-answer-letter', LETTERS[i] ?? String(i + 1), button);
      el('span', 'quiz-answer-text', answer, button);
      // pointerdown, not click: a tap answers at once – every hundredth of a second counts
      button.addEventListener('pointerdown', (event) => {
        event.preventDefault();
        event.stopPropagation();
        this.onAnswer(i);
      });
      return button;
    });
  }

  private showResult(session: QuizSession, shown: ShownQuestion): void {
    this.resultShown = true;
    const outcome = session.outcome;
    this.element.classList.add('is-result', outcome?.correct ? 'is-correct' : 'is-wrong');
    this.buttons.forEach((button, i) => {
      button.disabled = true;
      button.classList.toggle('is-right', i === shown.correct);
      button.classList.toggle('is-chosen', i === outcome?.chosen);
    });
    if (outcome?.correct) {
      this.bonus.textContent = formatNumber(outcome.bonus);
      this.result.textContent = `RICHTIG! +${formatNumber(outcome.bonus)} Flaschen`;
    } else {
      const penalty = `−${formatNumber(this.config.wrongPenalty)}`;
      this.bonus.textContent = penalty;
      this.result.textContent = `${outcome?.chosen === null ? 'ZEIT UM!' : 'LEIDER FALSCH'} ${penalty} Flaschen`;
    }
  }

  /** Marks an answer for the arrow keys (user 2026-10-04: choose with ↑/↓ or ←/→, confirm with Enter). */
  private select(index: number): void {
    this.selected = index;
    this.buttons.forEach((button, i) => button.classList.toggle('is-selected', i === index));
  }

  private readonly onKey = (event: KeyboardEvent): void => {
    if (this.element.hidden || this.suspended) return;
    // While the quiz is up, no key reaches the game (no lane change, no pause that would stop the clock).
    event.preventDefault();
    event.stopPropagation();
    if (this.resultShown || event.repeat) return;
    const count = this.buttons.length;
    if (count > 0 && SELECT_PREV.includes(event.key)) {
      this.select(this.selected < 0 ? count - 1 : (this.selected - 1 + count) % count);
      return;
    }
    if (count > 0 && SELECT_NEXT.includes(event.key)) {
      this.select(this.selected < 0 ? 0 : (this.selected + 1) % count);
      return;
    }
    if (CONFIRM.includes(event.key)) {
      if (this.selected >= 0) this.onAnswer(this.selected);
      return;
    }
    const index = this.config.answerKeys.findIndex((keys) => keys.some((k) => k.toLowerCase() === event.key.toLowerCase()));
    if (index >= 0) this.onAnswer(index);
  };
}
